import { EAggregators } from "utils/aggregators";
import { fetchApi, fetchWithTimeout, getJson, postJson, putJson } from "../../utils/api";
import { withTurnstileRetry } from "../../utils/turnstile";

export const SESSION_STORAGE_TOKEN = "bitsToken";

/**
 * Base URL for the address lookup calls only.
 *
 * They are a read-only proxy in front of Google Places — a query in, a
 * candidate address out — so they neither read nor write anything belonging to
 * an account, and they can safely be pointed at a different environment from
 * the rest of the app. That is the point of this override: the US DEV
 * environment was decommissioned in 2022 (see the commented-out `deploy-dev`
 * job in `.github/workflows/deploy-dev.yml`), so locally there is no dev host
 * to serve them.
 *
 * Unset — which is the case in every deployed build — this is `undefined` and
 * `fetchApi` falls back to `REACT_APP_BASE_API_URL` as before.
 */
const ADDRESS_LOOKUP_API_URL = process.env.REACT_APP_BASE_ADDRESS_API_URL || undefined;

export interface SubscriptionPlan {
  subscriptionPlanId: number;
  stripePlanId: string;
  name: string;
  title: string;
  subtitle: string;
  availability?: string;
  amount: number;
  currency: string;
  interval: string;
  creditAmount: number;
  order: number;
  isDefault: boolean;
  renewalPeriod?: number;
}

export interface UserInfo {
  address1?: string;
  address2?: string;
  address3?: string;
  addressId?: string;
  houseNumber?: string;
  city?: string;
  dateBirth?: string;
  email?: string;
  emergencyContact?: string;
  firstName?: string;
  gender?: string;
  lastName?: string;
  password?: string;
  phoneNumber?: string;
  phoneNumberToken?: string;
  postCode?: string;
  referredBy?: string;
  sessionId?: string;
  identifier?: string;
  membershipNumber?: string;
  country?: string;
  state?: string; // for us
  placeId?: string;
  userId?: string;
  termsAccepted?: boolean;
  title?: string;
  addressSuggestions?: AddressSuggestion[];
  referral?: string;
  aggregator?: EAggregators | undefined;
  aggregatorUid?: string | undefined;
  isAggregatorCustomer?: boolean;
  isCardBannerHidden?: boolean;
}

export interface RegistrationSessionUpdate {
  address1?: string;
  address2?: string;
  address3?: string;
  addressId?: string;
  city?: string;
  dateBirth?: string;
  emergencyContact?: string;
  firstName?: string;
  gender?: string;
  lastName?: string;
  password?: string;
  phoneNumber?: string;
  phoneNumberToken?: string;
  postCode?: string;
  referredBy?: string;
  title?: string;
}

export interface RegistrationSessionCreate extends RegistrationSessionUpdate {
  email: string;
}

export interface RegistrationSessionComplete extends RegistrationSessionUpdate {
  password: string;
  country?: string;
  state?: string; // for us
  placeId?: string;
}

export interface Address {
  address1?: string;
  address2?: string;
  address3?: string;
  city?: string;
  postCode?: string;
  country?: string;
  state?: string; // for us
  placeId?: string;
  addressId?: string;
}

export interface AddressSuggestion {
  suggestion?: string;
  address_id: string;
}

export interface AggregatorTrackingModel {
  aggregatorUid: string;
  aggregatorName: string;

  userId?: string;
  signUpDate?: string;

  subscribeDate?: string;
  unsubscribeDate?: string;
  planAmount?: number;

  creditCardApplicationId?: string; // eligibilityId ??
  creditCardApplicationStartedDate?: string;
  creditCardApplicationCompletedDate?: string;
  creditCardApplicationSuccess?: boolean;

  dateCreated?: string;
  dateUpdated?: string;
}

export class SignUpApi {
  async registerSession(payload: RegistrationSessionCreate, useTurnstile = false): Promise<UserInfo> {
    if (!useTurnstile) {
      return (await postJson("users/registration/session", payload)).json();
    }
    return (
      await withTurnstileRetry("signup-session-create", (headers) =>
        postJson("users/registration/v2/session", payload, undefined, headers)
      )
    ).json();
  }

  async updateSession(sessionId: string, payload: RegistrationSessionUpdate, useTurnstile = false): Promise<UserInfo> {
    if (!useTurnstile) {
      return (await putJson(`users/registration/session/${sessionId}`, payload)).json();
    }
    return (
      await withTurnstileRetry("signup-session-update", (headers) =>
        putJson(`users/registration/v2/session/${sessionId}`, payload, undefined, headers)
      )
    ).json();
  }

  async completeSession(
    sessionId: string,
    payload: RegistrationSessionComplete,
    useTurnstile = false
  ): Promise<string> {
    const request = useTurnstile
      ? withTurnstileRetry("signup-session-complete", (headers) =>
          postJson(`users/registration/v2/session/${sessionId}/complete`, payload, undefined, headers)
        )
      : postJson(`users/registration/session/${sessionId}/complete`, payload);
    return await request.then((response) => response.json()).then((response) => response["accessToken"]);
  }

  async createAndCompleteSession(userInfo: UserInfo, useTurnstile = false): Promise<string> {
    const payload: RegistrationSessionCreate = {
      email: userInfo.email || "",
    };
    const newUserInfoCreate = await this.registerSession(payload, useTurnstile);

    const payloadComplete: RegistrationSessionComplete = {
      address1: userInfo.address1,
      address2: userInfo.address2,
      address3: userInfo.address3,
      addressId: userInfo.addressId,
      city: userInfo.city,
      postCode: userInfo.postCode,
      dateBirth: userInfo.dateBirth,
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      emergencyContact: userInfo.emergencyContact,
      referredBy: userInfo.referredBy,
      phoneNumber: userInfo.phoneNumber,
      password: userInfo.password || "",
      country: userInfo.country,
      state: userInfo.state, // for us
      placeId: userInfo.placeId,
    };

    return await this.completeSession(newUserInfoCreate.sessionId || "", payloadComplete, useTurnstile);
  }

  async phoneVerifyRequest(phoneNumber: string, identifier: string): Promise<any> {
    return (await postJson("users/phone/verify_request", { identifier: identifier, phoneNumber: phoneNumber })).json();
  }

  async phoneVerify(token: string, identifier: string): Promise<any> {
    return (await postJson("users/phone/verify", { identifier: identifier, token: token })).json();
  }

  // Search address - UK only
  async searchAddress(query: string): Promise<AddressSuggestion[]> {
    return await postJson("address/search", { query: query })
      .then((response) => response.json())
      .then((response) => response["items"]);
  }

  async getAddress(addressId: string): Promise<Address> {
    return (
      await fetchApi(`address/${addressId}`, {
        method: "GET",
      })
    ).json();
  }

  async fetchProfile(): Promise<UserInfo> {
    return (await fetchApi(`me`, { method: "GET" })).json();
  }

  async unAuthorizedFetchProfile(token: string | undefined = undefined): Promise<Response> {
    let init: any = {};
    if (token && token !== "") {
      init = { headers: { Authorization: token, "Content-Type": "application/json" }, method: "GET" };
    }
    const url = `${process.env.REACT_APP_BASE_API_URL ?? "api/v1/"}me`;
    const response = await fetchWithTimeout(url, init);
    if (response.status >= 200 && response.status < 300) {
      return response;
    }

    const responseBodyMethod = (response.headers.get("content-type") || "").includes("json") ? "json" : "text";

    return Promise.reject({
      statusCode: response.status,
      error: (await response[responseBodyMethod]()) || response.statusText,
      requestMethod: init?.method || "GET",
      message: response.statusText,
    });
  }

  async validatePlaceId(placeId: string): Promise<any> {
    /* No leading slash: `fetchApi` concatenates the path onto the base URL,
       which already ends in one, so this produced `/api/v1//address/…`. Every
       other path in this file is relative. Harmless — the gateway normalises
       it — but the odd one out.

       `keepSessionOn401: true` because this call can be aimed at a different
       environment from the one the session belongs to; a 401 from the address
       service says nothing about whether the user is signed in, and without
       this it would clear their token. */
    return (await getJson(`address/${placeId}/google`, ADDRESS_LOOKUP_API_URL, true)).json();
  }

  async searchAddressGoogle(query: string): Promise<AddressSuggestion[]> {
    return await postJson("address/search/google", { query }, ADDRESS_LOOKUP_API_URL)
      .then((response) => response.json())
      .then((response) => response["items"]);
  }

  async saveEmailInMailchimpList(email: string) {
    return await postJson("email", { email }, process.env.REACT_APP_BASE_SIGNUP_EMAIL_STORAGE)
      .then((response) => response.json())
      .then((response) => response["data"]);
  }

  async tagListMember(email: string, activeSubscription = false, activeSupercharge = false) {
    return await putJson(
      `/email/${email}`,
      { activeSubscription, activeSupercharge },
      process.env.REACT_APP_BASE_SIGNUP_EMAIL_STORAGE
    )
      .then((response) => response.json())
      .then((response) => response["data"]);
  }

  async tagListMemberSupercharge(email: string) {
    return await this.tagListMember(email, false, true);
  }

  async tokenExchange(token: string): Promise<string> {
    return await postJson(`me/exchangeToken`, { token })
      .then((response) => response.json())
      .then((response) => response["accessToken"]);
  }

  async aggregatorTrackingInit(aggregatorName: string, aggregatorUid?: string): Promise<AggregatorTrackingModel> {
    return await postJson(`aggregator/init`, { aggregatorName, aggregatorUid })
      .then((response) => response.json())
      .then((response) => response["data"]);
  }
}
